/**
 * `<LocationInput>` — must-select Google Places Autocomplete picker.
 *
 * conversation-model-versioning Phase 6 Session 1 (2026-05-18).
 *
 * ADR 0024 (must-select invariant): the `onResolved` callback fires ONLY
 * after the user selects a prediction AND a `place_id` is captured.
 * There is NO `onChange(rawText)` path. Draft text is component-local
 * state and is NEVER emitted to a parent commit. An un-selected draft is
 * structurally un-committable.
 *
 * ADR 0017 (session token): the browser mints a
 * `google.maps.places.AutocompleteSessionToken` on mount; it is threaded
 * out via `onResolved.sessionToken` so the server resolver can join the
 * free Autocomplete session (Place Details call is bundled with the
 * Autocomplete requests under one token → one billable Essentials event).
 *
 * ADR 0022 (places-only loader): consumes `loadPlaces()`, never
 * `loadMaps()` — no Map ID required.
 *
 * Styling: Intelligence-Terminal tokens only (Ref #3 axis D).
 * No raw hex colours in this file.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { loadPlaces, GoogleMapsConfigError } from '@/lib/google-maps-loader';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface LocationResolvedValue {
  placeId: string;
  formattedAddress: string;
  sessionToken?: google.maps.places.AutocompleteSessionToken;
}

export interface LocationInputProps {
  conversationId: string;
  inputKey: string;
  initialValue?: string | null;
  onResolved: (v: LocationResolvedValue) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}

// ---------------------------------------------------------------------------
// Internal types for the Places New API
// ---------------------------------------------------------------------------

interface PlaceSuggestion {
  placeId: string;
  text: string;
}

// ---------------------------------------------------------------------------
// Debounce helper
// ---------------------------------------------------------------------------

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type LoadState = 'loading' | 'unavailable' | 'ready';

/**
 * Must-select Google Places Autocomplete picker (ADR 0024).
 *
 * Four UI states:
 * - `loading`     — `loadPlaces()` promise in-flight; input disabled.
 * - `unavailable` — `loadPlaces()` threw `GoogleMapsConfigError`; input
 *                   disabled with copy "Location search is unavailable."
 * - `ready`       — SDK loaded; live prediction list shown while typing.
 * - fetch error   — autocomplete call failed; inline "Couldn't load
 *                   suggestions. Try again." shown below the input.
 */
export function LocationInput({
  initialValue,
  onResolved,
  onCancel,
  autoFocus,
}: LocationInputProps) {
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [draft, setDraft] = useState(initialValue ?? '');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [fetchError, setFetchError] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  // Session token minted on mount (ADR 0017).
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesRef = useRef<typeof google.maps.places | null>(null);

  // Load the Places library on mount.
  useEffect(() => {
    let cancelled = false;
    loadPlaces()
      .then((places) => {
        if (cancelled) return;
        placesRef.current = places;
        sessionTokenRef.current = new places.AutocompleteSessionToken();
        setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof GoogleMapsConfigError) {
          setLoadState('unavailable');
        } else {
          // Unexpected SDK load failure — treat as unavailable.
          setLoadState('unavailable');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce draft text before firing autocomplete.
  const debouncedDraft = useDebouncedValue(draft, 250);

  // Fetch autocomplete suggestions whenever the debounced draft changes.
  useEffect(() => {
    if (loadState !== 'ready') return;
    if (debouncedDraft.trim().length < 2) {
      setSuggestions([]);
      setFetchError(false);
      return;
    }

    let cancelled = false;
    setFetchError(false);

    const run = async () => {
      const places = placesRef.current;
      if (!places) return;
      try {
        const { suggestions: raw } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: debouncedDraft,
            sessionToken: sessionTokenRef.current ?? undefined,
          });
        if (cancelled) return;
        const mapped: PlaceSuggestion[] = raw
          .filter((s) => s.placePrediction !== null)
          .map((s) => ({
            placeId: s.placePrediction!.placeId,
            text:
              s.placePrediction!.text?.toString() ?? s.placePrediction!.placeId,
          }));
        setSuggestions(mapped);
        setActiveSuggestionIndex(-1);
      } catch {
        if (!cancelled) {
          setFetchError(true);
          setSuggestions([]);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [debouncedDraft, loadState]);

  // Select a prediction — the ONLY path that emits `onResolved` (ADR 0024).
  const handleSelectSuggestion = useCallback(
    async (suggestion: PlaceSuggestion) => {
      setSelecting(true);
      const places = placesRef.current;
      if (!places) return;

      try {
        const place = new places.Place({ id: suggestion.placeId });
        await place.fetchFields({ fields: ['formattedAddress'] });
        const formattedAddress = place.formattedAddress ?? suggestion.text;

        onResolved({
          placeId: suggestion.placeId,
          formattedAddress,
          sessionToken: sessionTokenRef.current ?? undefined,
        });

        // Clear prediction list — selection is committed.
        setSuggestions([]);
        setDraft(formattedAddress);
      } catch {
        setFetchError(true);
      } finally {
        setSelecting(false);
      }
    },
    [onResolved]
  );

  // Keyboard navigation inside the prediction list.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (suggestions.length === 0) {
        if (e.key === 'Escape') onCancel?.();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex((i) =>
          Math.min(i + 1, suggestions.length - 1)
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const idx = activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0;
        const s = suggestions[idx];
        if (s) void handleSelectSuggestion(s);
      } else if (e.key === 'Escape') {
        setSuggestions([]);
        onCancel?.();
      }
    },
    [suggestions, activeSuggestionIndex, handleSelectSuggestion, onCancel]
  );

  const isDisabled =
    loadState === 'loading' || loadState === 'unavailable' || selecting;

  // Show a spinner while the Places SDK loads. Also surfaces during a
  // selection round-trip (place.fetchFields). The spinner is a sibling of
  // the input positioned absolute on the right edge so it doesn't disturb
  // input width or focus behavior.
  const showSpinner = loadState === 'loading' || selecting;

  return (
    <div className="relative w-full" data-testid="location-input-root">
      {/* Text input — draft only, never emitted directly (ADR 0024) */}
      <div className="relative">
        <Input
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setActiveSuggestionIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          autoFocus={autoFocus}
          placeholder={
            loadState === 'loading'
              ? 'Loading address search…'
              : loadState === 'unavailable'
              ? 'Location search is unavailable.'
              : 'Search for a property address…'
          }
          aria-label="Property address search"
          aria-autocomplete="list"
          aria-busy={showSpinner}
          aria-expanded={suggestions.length > 0}
          data-testid="location-input-field"
          className={showSpinner ? 'pr-8' : undefined}
        />
        {showSpinner && (
          <Loader2
            className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-accent animate-spin pointer-events-none"
            aria-hidden="true"
            data-testid="location-input-spinner"
          />
        )}
      </div>

      {/* Unavailable notice */}
      {loadState === 'unavailable' && (
        <p
          className="mt-1.5 font-mono text-xs text-text-muted"
          data-testid="location-input-unavailable"
        >
          Location search is unavailable.
        </p>
      )}

      {/* Fetch error */}
      {fetchError && loadState === 'ready' && (
        <p
          className="mt-1.5 font-mono text-xs text-warn"
          data-testid="location-input-fetch-error"
        >
          Couldn&apos;t load suggestions. Try again.
        </p>
      )}

      {/* Prediction list. 2026-05-26 — bg-void + border-panel-border-hi +
          drop shadow lifts the dropdown off the panel chrome (the prior
          bg-panel-bg was nearly identical to the row background). Hover +
          active states use bg-accent/15 for clear keyboard/mouse signal
          (the prior bg-accent/7 read as ~no change in dark mode). A footer
          hint inside the listbox surfaces the must-select-to-save invariant
          (ADR 0024) so users know clicking / Enter on a suggestion commits. */}
      {suggestions.length > 0 && loadState === 'ready' && (
        <div
          className={clsx(
            'absolute left-0 right-0 top-full mt-1 z-50',
            'bg-void border-2 border-panel-border-hi rounded-[3px]',
            'shadow-[0_8px_24px_rgba(0,0,0,0.55)]',
            'overflow-hidden'
          )}
        >
          <ul
            role="listbox"
            aria-label="Address suggestions"
            data-testid="location-input-suggestions"
            className="overflow-y-auto max-h-56"
          >
            {suggestions.map((s, idx) => (
              <li
                key={s.placeId}
                role="option"
                aria-selected={idx === activeSuggestionIndex}
                data-testid={`location-input-suggestion-${idx}`}
                onMouseDown={(e) => {
                  // Prevent input blur before click registers.
                  e.preventDefault();
                }}
                onClick={() => void handleSelectSuggestion(s)}
                className={clsx(
                  'px-3 py-2.5 cursor-pointer font-mono text-xs',
                  'border-b border-panel-border-hi last:border-b-0',
                  'transition-colors',
                  idx === activeSuggestionIndex
                    ? 'bg-accent/15 text-accent font-semibold'
                    : 'text-text-bright hover:bg-accent/10 hover:text-accent'
                )}
              >
                {s.text}
              </li>
            ))}
          </ul>
          <div
            className="px-3 py-1.5 border-t border-panel-border-hi bg-panel-bg font-mono text-[9px] text-text-muted tracking-[0.5px] uppercase"
            data-testid="location-input-hint"
          >
            ↑↓ navigate · Enter / click to save · Esc to cancel
          </div>
        </div>
      )}
    </div>
  );
}
